import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Paper, Chip, IconButton, Tooltip, Grid, CircularProgress, Alert, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { alpha } from '@mui/material/styles';
import {
  getPreviousLoans, addPreviousLoan, updatePreviousLoan, deletePreviousLoan,
} from '../../services/studentService';

const LOAN_STATUSES = [
  { value: 'applied', label: 'Applied', color: '#6366F1' },
  { value: 'under_review', label: 'Under Review', color: '#3B82F6' },
  { value: 'approved', label: 'Approved', color: '#10B981' },
  { value: 'sanctioned', label: 'Sanctioned', color: '#06B6D4' },
  { value: 'rejected', label: 'Rejected', color: '#EF4444' },
  { value: 'disbursed', label: 'Disbursed', color: '#10B981' },
  { value: 'closed', label: 'Closed', color: '#6B7280' },
];

const getStatusConfig = (status) =>
  LOAN_STATUSES.find((s) => s.value === status) || { label: status, color: '#6B7280' };

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const emptyForm = {
  bankName: '',
  loanId: '',
  loanAmount: '',
  status: 'applied',
  rejectionReason: '',
  appliedDate: '',
  remarks: '',
};

const PreviousLoans = ({ studentId }) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchLoans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPreviousLoans(studentId);
      setLoans(res.data?.data?.previousLoans || []);
    } catch (err) {
      console.error('Failed to fetch previous loans:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const openAddDialog = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (loan) => {
    setForm({
      bankName: loan.bankName || '',
      loanId: loan.loanId || '',
      loanAmount: loan.loanAmount || '',
      status: loan.status || 'applied',
      rejectionReason: loan.rejectionReason || '',
      appliedDate: loan.appliedDate ? new Date(loan.appliedDate).toISOString().split('T')[0] : '',
      remarks: loan.remarks || '',
    });
    setEditingId(loan._id);
    setFormError('');
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.bankName.trim()) {
      setFormError('Bank name is required');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        loanAmount: form.loanAmount ? Number(form.loanAmount) : undefined,
        appliedDate: form.appliedDate || undefined,
      };

      if (editingId) {
        await updatePreviousLoan(studentId, editingId, payload);
      } else {
        await addPreviousLoan(studentId, payload);
      }

      setDialogOpen(false);
      fetchLoans();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (loanId) => {
    try {
      await deletePreviousLoan(studentId, loanId);
      setDeleteConfirm(null);
      fetchLoans();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceRounded sx={{ color: '#6366F1', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Previous Loan Applications
          </Typography>
          {loans.length > 0 && (
            <Chip
              label={loans.length}
              size="small"
              sx={{
                height: 22, fontSize: '0.7rem', fontWeight: 700,
                backgroundColor: alpha('#6366F1', 0.1), color: '#6366F1',
              }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRounded />}
          onClick={openAddDialog}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
        >
          Add Loan Record
        </Button>
      </Box>

      {/* Loan Cards */}
      {loans.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}>
          <AccountBalanceRounded sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
          <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
            No previous loan applications recorded yet.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {loans.map((loan) => {
            const config = getStatusConfig(loan.status);
            return (
              <Grid size={{ xs: 12, md: 6 }} key={loan._id}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 2.5,
                    border: '1px solid #E5E7EB',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: alpha(config.color, 0.3),
                      boxShadow: `0 2px 8px ${alpha(config.color, 0.08)}`,
                    },
                  }}
                >
                  {/* Top row: Bank + Status + Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', mb: 0.25 }}>
                        {loan.bankName}
                      </Typography>
                      {loan.loanId && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                          Loan ID: {loan.loanId}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          height: 24, fontSize: '0.7rem', fontWeight: 600,
                          color: config.color,
                          backgroundColor: alpha(config.color, 0.1),
                          border: `1px solid ${alpha(config.color, 0.2)}`,
                        }}
                      />
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(loan)}>
                          <EditRounded sx={{ fontSize: 16, color: '#9CA3AF' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteConfirm(loan._id)}>
                          <DeleteRounded sx={{ fontSize: 16, color: '#9CA3AF' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Details Grid */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: loan.rejectionReason || loan.remarks ? 1.5 : 0 }}>
                    {loan.loanAmount > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                          Amount
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                          {formatCurrency(loan.loanAmount)}
                        </Typography>
                      </Box>
                    )}
                    {loan.appliedDate && (
                      <Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                          Applied Date
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                          {formatDate(loan.appliedDate)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Rejection Reason */}
                  {loan.rejectionReason && (
                    <Box
                      sx={{
                        mt: 1, px: 1.5, py: 1, borderRadius: 1.5,
                        backgroundColor: alpha('#EF4444', 0.05),
                        border: `1px solid ${alpha('#EF4444', 0.15)}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.65rem', color: '#EF4444', fontWeight: 600, mb: 0.25 }}>
                        REJECTION REASON
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#7F1D1D', lineHeight: 1.5 }}>
                        {loan.rejectionReason}
                      </Typography>
                    </Box>
                  )}

                  {/* Remarks */}
                  {loan.remarks && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 600, mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Remarks
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#4B5563', lineHeight: 1.5 }}>
                        {loan.remarks}
                      </Typography>
                    </Box>
                  )}

                  {/* Footer */}
                  <Typography sx={{ fontSize: '0.68rem', color: '#D1D5DB', mt: 1.5 }}>
                    Added {formatDate(loan.addedAt)} by {loan.addedBy?.name || 'Unknown'}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Add / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {editingId ? 'Edit Loan Record' : 'Add Previous Loan'}
          </Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" label="Bank Name" required
                value={form.bankName} onChange={handleChange('bankName')}
                placeholder="e.g. SBI, HDFC, ICICI"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" label="Loan ID / Reference"
                value={form.loanId} onChange={handleChange('loanId')}
                placeholder="e.g. LN-2025-12345"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" label="Loan Amount" type="number"
                value={form.loanAmount} onChange={handleChange('loanAmount')}
                placeholder="e.g. 2500000"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={handleChange('status')}>
                  {LOAN_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.color }} />
                        {s.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" label="Applied Date" type="date"
                value={form.appliedDate} onChange={handleChange('appliedDate')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Rejection Reason" multiline rows={2}
                value={form.rejectionReason} onChange={handleChange('rejectionReason')}
                placeholder="If rejected, enter the reason..."
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Remarks" multiline rows={2}
                value={form.remarks} onChange={handleChange('remarks')}
                placeholder="Any additional notes..."
              />
            </Grid>
          </Grid>
          {formError && (
            <Alert severity="error" sx={{ mt: 2, py: 0 }}>{formError}</Alert>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined" size="small" onClick={() => setDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" size="small" onClick={handleSubmit} disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {submitting ? 'Saving...' : editingId ? 'Update' : 'Add Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Delete Loan Record</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: '#4B5563' }}>
            Are you sure you want to delete this loan record? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" size="small" onClick={() => setDeleteConfirm(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" size="small" onClick={() => handleDelete(deleteConfirm)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PreviousLoans;
